import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BaseLayout, Icon, Menu, useIsMobile, type MenuWrapperItem } from '@insio/ui-toolkit';
import { useTranslation } from 'react-i18next';
import { navigation } from '../navigation';
import { MasterAccountBar, MembershipSource, useAuth, usePermissions } from '@/features/auth';
import { canOpenPayments } from '@/features/payments/utils/payments-menu';
import { useAccountRelationName } from '@/hooks/use-account-relation-name';
import { useExtractionTracking } from '@/hooks/use-extraction-tracking';
import { useInvoiceLiveRefresh } from '@/features/invoices/hooks/use-invoice-live-refresh';
import { NotificationCenterContainer, NotificationsProvider } from '@/features/notifications';
import { canReceiveInbox, inboxMenuItem, useInboxSummary } from '@/features/inbox';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { OnboardingRedirect, useIsOnboardingActive } from '@/features/onboarding';
import { FeatureFlagKey, useFeatureFlag } from '@/features/feature-flags';
import { AiSidebar, useAiChatContext } from 'juneau';
import { AiChatDisplayMode } from '@insio/shared-types';
import { renderPart } from '@/features/ai-chat/parts/part-registry';
import { AI_SESSION_ID } from '@/features/ai-chat/session';
import { AiChatWithHistory } from '@/features/ai-chat/AiChatWithHistory';
import { DockedChatPanel } from '@/features/ai-chat/DockedChatPanel';
import { useChatDisplayMode } from '@/features/ai-chat/use-chat-display-mode';
import { TappeerEMark } from '@/features/ai-chat/TappeerBrandMark';
import { createGraphqlWsAdapter } from '@/features/ai-chat/graphql-ws-adapter';
import { ChatSettingsButton } from '@/features/ai-chat/ChatSettingsButton';
import { OnlineIndicator } from '@/features/ai-chat/OnlineIndicator';
import { GetStartedState } from '@/features/ai-chat/GetStartedState';
import { useChatReset } from '@/features/ai-chat/use-chat-reset';
import { useChatInputActions } from '@/features/ai-chat/use-chat-input-actions';
import { useGreetingTrigger } from '@/features/ai-chat/use-greeting-trigger';
import { useNudgeSignal } from '@/features/ai-chat/nudges/use-nudge-signal';
import { ChatNudgeSignal } from '@insio/shared-types';
import { useAiChatSimplifiedMode } from '@/features/ai-chat/use-ai-chat-simplified-mode';
import { useChatPanelState } from '@/features/ai-chat/use-chat-panel-state';
import { useUnreadWatcher } from '@/features/ai-chat/use-unread-watcher';
import { ChatLauncherButton } from '@/features/ai-chat/ChatLauncherButton';
import { isFullPageChatPath } from '@/features/ai-chat/full-page-chat-path';
import { menuLink, menuLinkClassName } from './chrome/menu-link';
import { AccountSwitchConfirm, SessionChrome } from './chrome/SessionChrome';
import { useProfileMenu } from './chrome/use-profile-menu';
import { useSettingsSections } from '@/components/settings-shell/use-settings-sections';

const chatAdapter = createGraphqlWsAdapter();

/**
 * Lets the assistant speak first after sign-in. Mounted inside the chat provider so
 * it can reach sendGreeting; the panel is already open, so nothing else is needed.
 */
function AiGreetingGuard() {
  const { user } = useAuth();
  const simplifiedMode = useAiChatSimplifiedMode();

  useGreetingTrigger({
    userId: user?.id ?? null,
    // Absent means on — the greeting is opt-out, matching the server-side default.
    // Simplified mode never greets: juneau adds an empty message before the server refuses.
    enabled: user?.currentPreferences?.greetingEnabled !== false && !simplifiedMode
  });

  return null;
}

/**
 * Lights the header badge when the assistant speaks into a closed chat.
 * Must render inside the chat provider to observe the stream.
 */
function AiUnreadGuard({ closed, markUnread }: { closed: boolean; markUnread: () => void }) {
  useUnreadWatcher({ closed, markUnread });
  return null;
}

/**
 * Asks for an offer an account event queued, each time the chat comes on screen. Must render
 * inside the chat provider; the server decides whether anything is offered.
 */
function AiNudgeGuard({ closed }: { closed: boolean }) {
  const signalNudge = useNudgeSignal();
  useEffect(() => {
    if (!closed) signalNudge(ChatNudgeSignal.CHAT_OPENED);
    // Keyed on opening only — a new chat context must not ask again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closed]);
  return null;
}

/** Syncs current language, route and session into the Juneau AI context on every navigation. */
function AiContextSync() {
  const { setContext } = useAiChatContext();
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    setContext(prev => ({
      ...prev,
      sessionId: AI_SESSION_ID,
      language: i18n.language,
      route: location.pathname
    }));
  }, [location.pathname, i18n.language, setContext]);

  return null;
}

/** Mounted once per session so extractions are tracked from any page. */
function ExtractionTrackingGuard() {
  useExtractionTracking();
  return null;
}

/** Mounted once per session so a finished extraction refreshes the invoice list from any page. */
function InvoiceLiveRefreshGuard() {
  useInvoiceLiveRefresh();
  return null;
}

interface AuthenticatedLayoutProps {
  children: ReactNode;
}

/**
 * Full authenticated Tappeer chrome: top menu bar, AI chat, extraction dock and the shared
 * session chrome. Wraps the routed content of every account that is not a master account board.
 */
export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();

  const isOnboardingActive = useIsOnboardingActive();
  const isProcurementEnabled = useFeatureFlag(FeatureFlagKey.PROCUREMENT);
  const isPaymentsEnabled = useFeatureFlag(FeatureFlagKey.PAYMENTS);
  const { confirmReset, clearServerHistory, dialog: resetConfirmDialog } = useChatReset();
  const { actions: chatInputActions, dialogs: chatInputActionDialogs } = useChatInputActions();
  const isMobile = useIsMobile();
  const settingsSections = useSettingsSections();
  // The plan links to its section exactly where System settings would open it (a permission only).
  const opensPlan = settingsSections.sections.some(section => section.id === 'subscription');
  const canOpenAutomation = settingsSections.sections.some(section => section.id === 'automation');
  const { profileMenuProps, switchPrompt } = useProfileMenu(
    opensPlan ? navigation.settings('subscription') : undefined
  );
  const clientName = useAccountRelationName();
  // Inside a client reached through the office, the logo names that client.
  const clientLabel =
    user?.source === MembershipSource.MASTER_ACCOUNT && user.account
      ? clientName(user.account.name, user.account.isMasterAccountOwnBooks)
      : undefined;

  // The full-page assistant (`/` and `/tappeer-ai`) is on screen.
  const homeChatActive = isFullPageChatPath(location.pathname);
  const documentsActive = location.pathname.startsWith(navigation.documents());
  // "Doklady": the invoice list, receipt detail, contracts, and the three procurement lists by
  // exact path (a detail under them belongs to "Nákup").
  const onProcurementList = [
    navigation.requisitions(),
    navigation.purchaseOrders(),
    navigation.goodsReceipts()
  ].includes(location.pathname);
  const onContracts =
    location.pathname === navigation.contracts() ||
    location.pathname.startsWith(`${navigation.contracts()}/`);
  const invoicesActive =
    location.pathname.startsWith(navigation.invoices()) ||
    location.pathname.startsWith('/receipts/') ||
    onProcurementList ||
    onContracts;

  const procurementActive =
    location.pathname.startsWith(navigation.procurement()) && !onProcurementList;
  const bankMovementsActive = location.pathname.startsWith(navigation.bankMovements());
  const paymentsActive = location.pathname.startsWith(navigation.payments());
  const inboxActive = location.pathname.startsWith(navigation.notifications());
  const settingsActive =
    location.pathname === navigation.settings() ||
    location.pathname.startsWith(`${navigation.settings()}/`);
  const inboxSummary = useInboxSummary({ enabled: canReceiveInbox(user) });
  const tInbox = useFeatureTranslation('inbox');

  // Every desktop chat surface is user-selectable; mobile always falls back to
  // the full-page chat, and HIDDEN drops the surface entirely. The full-page chat
  // (`/` and `/tappeer-ai`) is the same conversation, so a panel there would render it twice.
  const displayMode = useChatDisplayMode();
  const chatSurfaceAllowed = !homeChatActive && !isMobile;
  const sidebarVisible = chatSurfaceAllowed && displayMode === AiChatDisplayMode.BUBBLE;
  const dockedVisible = chatSurfaceAllowed && displayMode === AiChatDisplayMode.DOCKED;

  const { minimized, setMinimized, unread, markUnread, clearUnread } = useChatPanelState(
    user?.id ?? null
  );
  // Landing on the chat home reads whatever the badge was holding; an expanded
  // docked column is on screen, so it reads continuously.
  const dockedOpen = dockedVisible && !minimized;
  useEffect(() => {
    if (homeChatActive || dockedOpen) clearUnread();
  }, [homeChatActive, dockedOpen, clearUnread]);

  // The home page and an expanded docked column are conversations on screen too —
  // only an absent or collapsed surface leaves a finished turn unread.
  const chatClosed = !homeChatActive && !dockedOpen && (!sidebarVisible || minimized);

  // Ordered by how the day goes: the procurement overview first, then the document lists, then
  // extraction. Procurement is behind its flag: the section is one entry that grows a sub-nav
  // once the queue and the per-type lists land, so it goes in as a single item rather than a
  // group of one.
  const menuItems: MenuWrapperItem[] = [];
  if (isProcurementEnabled) {
    menuItems.push({
      label: t('features.procurement.title'),
      icon: 'bag-shopping',
      href: navigation.procurement(),
      isActive: procurementActive,
      item: (
        <Link to={navigation.procurement()} className={menuLinkClassName}>
          <Icon
            name='bag-shopping'
            className={procurementActive ? 'text-secondary-base' : 'text-foreground-base'}
          />
          <span
            className={`font-medium ${procurementActive ? 'text-secondary-base' : 'text-foreground-base'}`}
          >
            {t('features.procurement.title')}
          </span>
        </Link>
      )
    });
  }
  menuItems.push(
    menuLink(
      t('features.invoices.list.title'),
      'file-invoice-dollar',
      navigation.invoices(),
      invoicesActive
    ),
    // What we are paying, between the documents and what actually moved (§11.2); the section and
    // the bank movements below stand behind the payments flag.
    ...(isPaymentsEnabled && canOpenPayments(hasPermission)
      ? [menuLink(t('features.payments.title'), 'coins', navigation.payables(), paymentsActive)]
      : []),
    // Where the invoices' money went: right after the documents it pays.
    ...(isPaymentsEnabled
      ? [
          menuLink(
            t('features.bankMovements.title'),
            'wallet',
            navigation.bankMovements(),
            bankMovementsActive
          )
        ]
      : []),
    menuLink(t('features.documents.title'), 'file-lines', navigation.documents(), documentsActive, {
      'data-onboarding': 'next-document'
    })
  );

  // Shown only while the inbox holds anything, so master account members and accounts nobody asks
  // questions never see it.
  const inboxItem = inboxMenuItem({
    summary: inboxSummary,
    isActive: inboxActive,
    t: tInbox,
    className: menuLinkClassName
  });
  if (inboxItem) menuItems.push(inboxItem);

  // The full-page chat is the home page, reached through the logo rather than its own
  // menu entry — listing it would duplicate that link.

  const layout = (
    <div className='flex flex-col h-dvh'>
      <AiContextSync />
      <AiGreetingGuard />
      <AiUnreadGuard closed={chatClosed} markUnread={markUnread} />
      <AiNudgeGuard closed={chatClosed} />
      <ExtractionTrackingGuard />
      <InvoiceLiveRefreshGuard />
      <OnboardingRedirect />
      <SessionChrome />
      <MasterAccountBar />
      <AccountSwitchConfirm prompt={switchPrompt} />
      {/* Extraction dock — app-wide; it self-hides when there is nothing to report. */}
      {!isOnboardingActive && <NotificationCenterContainer />}
      <BaseLayout orientation='horizontal' className='flex-1 min-h-0 h-full'>
        <BaseLayout.Menu>
          <Menu
            {...profileMenuProps}
            label={clientLabel}
            items={menuItems}
            showLogo={true}
            showProfile={true}
            orientation='horizontal'
            logoHref={navigation.dashboard()}
            // Every role opens it; the shell shows each role only its own sections.
            systemSettingsHref={navigation.settings()}
            // The other System settings tabs; the code lists' menu opens Firmy on a desktop.
            codeListsHref={navigation.codeListsMenu()}
            automationHref={canOpenAutomation ? navigation.settings('automation') : undefined}
            isSettingsActive={settingsActive}
          />
        </BaseLayout.Menu>
        <BaseLayout.Content className='flex flex-col min-h-0'>
          {/* The docked copilot takes its own column — content shrinks, nothing overlays. */}
          <div className='flex min-h-0 flex-1 flex-row overflow-hidden'>
            <div className='min-w-0 flex-1 overflow-hidden'>{children}</div>
            {dockedVisible && (
              <DockedChatPanel collapsed={minimized} onCollapsedChange={setMinimized} />
            )}
          </div>
        </BaseLayout.Content>
      </BaseLayout>
      {/* The full-page chat is the same conversation — showing both would overlap it. */}
      {/* Collapsed, the round launcher replaces the panel; the conversation lives in the
          provider above, so unmounting the panel loses nothing. */}
      {sidebarVisible && minimized && (
        <ChatLauncherButton unread={unread} onOpen={() => setMinimized(false)} />
      )}
      {sidebarVisible && !minimized && (
        <AiSidebar
          icon={<TappeerEMark size={18} chipTone='hero' />}
          height='75vh'
          style={{ width: '520px' }}
          minimized={false}
          onMinimizedChange={setMinimized}
          onReset={clearServerHistory}
          confirmReset={confirmReset}
          renderPart={renderPart}
          actions={chatInputActions}
          headerActions={
            <>
              <OnlineIndicator />
              <ChatSettingsButton />
            </>
          }
          emptyState={<GetStartedState compact />}
        />
      )}
      {sidebarVisible && resetConfirmDialog}
      {sidebarVisible && chatInputActionDialogs}
    </div>
  );

  return (
    <NotificationsProvider>
      <AiChatWithHistory adapter={chatAdapter}>{layout}</AiChatWithHistory>
    </NotificationsProvider>
  );
}

export default AuthenticatedLayout;
